import React from 'react';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import type { WealthicaPosition } from '@/providers/wealthica2/types';
import type { GenericPosition } from './useAddPositionsDialog';

interface AddPositionsListProps {
  selectedPositions: (WealthicaPosition | GenericPosition)[];
  showAllPositions: boolean;
  setShowAllPositions: (open: boolean) => void;
  onRemove: (positionId: string) => void;
}

export function AddPositionsList({
  selectedPositions,
  showAllPositions,
  setShowAllPositions,
  onRemove,
}: AddPositionsListProps) {
  const count = selectedPositions?.length || 0;
  const positionsToShow = showAllPositions ? (selectedPositions || []) : (selectedPositions || []).slice(0, 10);

  const getDisplayLabel = (p: WealthicaPosition | GenericPosition): string => {
    // Prefer symbol when present
    const symbol = 'symbol' in p && typeof p.symbol === 'string' ? p.symbol : undefined;
    if (symbol && symbol.length > 0) return symbol;
    // Fallback to name (GenericPosition always has name)
    const name = 'name' in p && typeof p.name === 'string' ? p.name : undefined;
    return name && name.length > 0 ? name : 'Unknown';
  };

  return (
    <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md text-sm">
      <p className="text-slate-700 font-medium mb-2">Selected Positions ({count})</p>
      <div className={`flex flex-wrap gap-2 ${showAllPositions ? 'max-h-none' : 'max-h-24'} overflow-y-auto`}>
        {positionsToShow.map((position) => (
          <Badge key={position.id} variant="outline" className="bg-white flex items-center gap-1 pr-1">
            <span>{getDisplayLabel(position)}</span>
            <button
              onClick={() => onRemove(position.id)}
              className="ml-1 hover:bg-gray-200 rounded-full p-0.5 transition-colors"
              title="Remove position"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      {count > 10 && (
        <button onClick={() => setShowAllPositions(!showAllPositions)} className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline">
          {showAllPositions ? 'Show less' : `Show ${count - 10} more`}
        </button>
      )}
    </div>
  );
}
